import { animate, inView } from "motion/react"

import { isMobile } from "@utils/V2/screen"
import { useCallback, useEffect, useRef } from "react"

const FADE_UP_DURATION_SECONDS = 2
// Matches the previous CSS keyframe's cubic-bezier(.13,.89,.18,.97)
const FADE_UP_EASE = [0.13, 0.89, 0.18, 0.97] as const

/**
 * Fades an element up into view via motion's `inView` when it scrolls into
 * the viewport, staggering by the element's index among its DOM siblings.
 */
export const useScrollAnimateSelf = (
  shouldAnimate: boolean,
  /** Lowers the mobile intersection threshold to 0 for slider children */
  isSliderChild: boolean
) => {
  const stopWatchingRef = useRef<VoidFunction | null>(null)

  const animationRefCallback = useCallback(
    (node: HTMLElement | null) => {
      if (
        !shouldAnimate ||
        !node ||
        process.env.GATSBY_IS_AOS_DISABLED === "true"
      )
        return

      const rect = node.getBoundingClientRect()
      const isAboveTheFold = rect.top < globalThis.innerHeight
      if (isAboveTheFold) {
        node.classList.add("testid-scroll-animate")
        return
      }

      node.classList.add("testid-scroll-animate", "opacity-0")

      stopWatchingRef.current?.()

      stopWatchingRef.current = inView(
        node,
        () => {
          const childIndex = node.parentElement
            ? Array.from(node.parentElement.children).indexOf(node)
            : 0

          animate(
            node,
            { opacity: [0, 1], y: [100, 0] },
            {
              duration: FADE_UP_DURATION_SECONDS,
              ease: FADE_UP_EASE,
              delay: isMobile() ? 0 : (childIndex * 200) / 1000,
            }
          )

          stopWatchingRef.current?.()
        },
        {
          margin: "0%",
          amount: isMobile() && isSliderChild ? 0 : 0.3,
        }
      )
    },
    [isSliderChild, shouldAnimate]
  )

  useEffect(() => {
    return () => {
      stopWatchingRef.current?.()
    }
  }, [])

  return animationRefCallback
}
